import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { easings } from "@funnel-ui/motion";

export type StepTransitionMode = "slide" | "fade" | "modal_overlay";
export type StepTransitionDirection = "forward" | "back";

type StepTransitionProps = {
  stepKey: string;
  direction?: StepTransitionDirection;
  mode?: StepTransitionMode;
  children: ReactNode;
  onAnimationComplete?: () => void;
  className?: string;
  testId?: string;
};

const slideVariants = {
  enter: (direction: StepTransitionDirection) => ({
    x: direction === "forward" ? 32 : -32,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: StepTransitionDirection) => ({
    x: direction === "forward" ? -32 : 32,
    opacity: 0,
  }),
};

const fadeVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  enter: { y: 24, opacity: 0 },
  center: { y: 0, opacity: 1 },
  exit: { y: 24, opacity: 0 },
};

export const StepTransition = ({
  stepKey,
  direction = "forward",
  mode = "slide",
  children,
  onAnimationComplete,
  className,
  testId,
}: StepTransitionProps) => {
  const prefersReduced = useReducedMotion();
  const variants =
    mode === "fade" ? fadeVariants : mode === "modal_overlay" ? modalVariants : slideVariants;
  const duration = prefersReduced ? 0 : 0.22;

  return (
    <div data-testid={testId} className={`relative flex flex-1 flex-col ${className ?? ""}`}>
      {mode === "modal_overlay" && (
        <AnimatePresence initial={false}>
          <m.div
            key={`${stepKey}-backdrop`}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration, ease: easings.emphasized }}
            style={{ background: "rgba(0,0,0,1)" }}
          />
        </AnimatePresence>
      )}
      <AnimatePresence mode="popLayout" custom={direction} initial={false}>
        <m.div
          key={stepKey}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration, ease: easings.emphasized }}
          onAnimationComplete={onAnimationComplete}
          className="relative z-10 flex flex-1 flex-col will-change-transform"
        >
          {children}
        </m.div>
      </AnimatePresence>
    </div>
  );
};

export default StepTransition;
